import React, { useEffect, useMemo, useState } from 'react';
import { relazioniAPI, opportunitiesAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClientCombobox } from '../ClientCombobox';
import { RelazioneAttachments } from './RelazioneAttachments';
import { Loader2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/apiError';

const empty = { title: '', country: '', client_ids: [], opportunity_id: '', date_from: '', date_to: '', analisi_commerciale: '', analisi_tecnica: '', analisi_amministrativa: '' };

const ANALISI = [
    { key: 'analisi_commerciale', label: 'ANALISI COMMERCIALE', hint: 'Esito incontri, esigenze del cliente, concorrenza, opportunità...' },
    { key: 'analisi_tecnica', label: 'ANALISI TECNICA', hint: 'Richieste tecniche, allestimenti, problemi riscontrati, soluzioni...' },
    { key: 'analisi_amministrativa', label: 'ANALISI AMMINISTRATIVA', hint: 'Pagamenti, documenti, condizioni contrattuali, scadenze...' },
];

export const RelazioneFormDialog = ({ open, onOpenChange, relazione, prefill, clients, countries, onSaved }) => {
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [savedId, setSavedId] = useState(null);
    const [pendingClient, setPendingClient] = useState('');
    const [opps, setOpps] = useState([]);

    useEffect(() => {
        if (!open) return;
        setSavedId(relazione?.id || null);
        setForm(relazione ? {
            title: relazione.title || '', country: relazione.country || '', client_ids: relazione.client_ids || [],
            opportunity_id: relazione.opportunity_id || '', date_from: (relazione.date_from || '').slice(0, 10), date_to: (relazione.date_to || '').slice(0, 10),
            analisi_commerciale: relazione.analisi_commerciale || '', analisi_tecnica: relazione.analisi_tecnica || '', analisi_amministrativa: relazione.analisi_amministrativa || '',
        } : { ...empty, ...(prefill || {}) });
    }, [open, relazione, prefill]);

    useEffect(() => {
        if (!open) return;
        opportunitiesAPI.getAll().then(r => setOpps(r.data || [])).catch(() => setOpps([]));
    }, [open]);

    const countryClients = useMemo(() => {
        const base = form.country ? clients.filter(c => (c.country || '').trim().toLowerCase() === form.country.trim().toLowerCase()) : clients;
        return base.filter(c => !form.client_ids.includes(c.id));
    }, [clients, form.country, form.client_ids]);

    const selectedClients = form.client_ids.map(id => clients.find(c => c.id === id)).filter(Boolean);
    const linkedOpps = useMemo(() => {
        const list = form.client_ids.length ? opps.filter(o => form.client_ids.includes(o.client_id)) : opps;
        return list.slice(0, 200);
    }, [opps, form.client_ids]);

    const addClient = (id) => {
        if (!id || form.client_ids.includes(id)) return;
        setForm(f => ({ ...f, client_ids: [...f.client_ids, id] }));
        setPendingClient('');
    };

    const handleSave = async (closeAfter = true) => {
        if (!form.title.trim()) return toast.error('Inserisci un titolo');
        if (!form.country) return toast.error('Seleziona la nazione');
        setSaving(true);
        try {
            const payload = { ...form, opportunity_id: form.opportunity_id || null, date_from: form.date_from || null, date_to: form.date_to || null };
            const res = savedId ? await relazioniAPI.update(savedId, payload) : await relazioniAPI.create(payload);
            setSavedId(res.data.id);
            toast.success(savedId ? 'Relazione aggiornata' : 'Relazione salvata');
            onSaved?.(res.data);
            if (closeAfter) onOpenChange(false);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Errore nel salvataggio'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="relazione-form-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading uppercase">{savedId ? 'Modifica relazione' : 'Nuova relazione'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Titolo *</Label>
                        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Viaggio Polonia — visita concessionari" data-testid="relazione-title" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Nazione *</Label>
                            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                <SelectTrigger data-testid="relazione-country"><SelectValue placeholder="Seleziona nazione" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Dal</Label>
                            <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="relazione-date-from" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Al</Label>
                            <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="relazione-date-to" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Clienti visitati {form.country && <span className="normal-case text-muted-foreground">(filtrati per {form.country})</span>}</Label>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <ClientCombobox clients={countryClients} value={pendingClient} onChange={addClient} placeholder="Aggiungi cliente..." testid="relazione-client-picker" />
                            </div>
                        </div>
                        {selectedClients.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2" data-testid="relazione-selected-clients">
                                {selectedClients.map(c => (
                                    <Badge key={c.id} variant="secondary" className="gap-1 pr-1">
                                        {c.company_name}{c.city ? ` · ${c.city}` : ''}
                                        <button type="button" onClick={() => setForm(f => ({ ...f, client_ids: f.client_ids.filter(x => x !== c.id) }))} className="ml-1 rounded-full hover:bg-muted p-0.5" data-testid={`relazione-remove-client-${c.id}`}>
                                            <X size={12} />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Commessa / Preventivo collegato</Label>
                        <Select value={form.opportunity_id || 'none'} onValueChange={(v) => setForm({ ...form, opportunity_id: v === 'none' ? '' : v })}>
                            <SelectTrigger data-testid="relazione-opportunity"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="none">Nessuno</SelectItem>
                                {linkedOpps.map(o => (
                                    <SelectItem key={o.id} value={o.id}>
                                        {o.kind === 'preventivo' ? 'Prev. ' : 'Comm. '}{o.title}{o.commessa ? ` · ${o.commessa}` : ''}{o.telaio ? ` · ${o.telaio}` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {ANALISI.map(a => (
                        <div key={a.key}>
                            <Label className="text-xs uppercase tracking-wider font-heading">{a.label}</Label>
                            <Textarea rows={5} value={form[a.key]} onChange={(e) => setForm({ ...form, [a.key]: e.target.value })} placeholder={a.hint} data-testid={`relazione-${a.key}`} />
                        </div>
                    ))}

                    <div className="border-t pt-4">
                        <Label className="text-xs uppercase tracking-wider">Allegati (foto, PDF, documenti)</Label>
                        {savedId ? (
                            <RelazioneAttachments relazioneId={savedId} canEdit />
                        ) : (
                            <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground mt-1">
                                <span>Salva la relazione per poter caricare gli allegati.</span>
                                <Button type="button" variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving} data-testid="relazione-save-continue">
                                    {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />} Salva e aggiungi allegati
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="relazione-cancel">Chiudi</Button>
                        <Button className="btn-primary" onClick={() => handleSave(true)} disabled={saving} data-testid="relazione-save">
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />} {savedId ? 'Salva modifiche' : 'Salva relazione'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default RelazioneFormDialog;
